// Shopify's existing product-form owns the only add request and drawer refresh.
class RaithCartUpsellCard extends HTMLElement {
  connectedCallback() {
    this.events?.abort(); this.events = new AbortController();
    const signal = this.events.signal;
    this.form = this.querySelector('form'); this.select = this.querySelector('select[name="id"]'); this.button = this.querySelector('[type="submit"]');
    if (!this.form || !this.select || !this.button) return;
    const update = () => {
      this.button.disabled = this.pending || !this.select.value || this.select.selectedOptions[0]?.disabled;
      const price = this.querySelector(`template[data-upsell-variant-price="${this.select.value}"]`);
      if (price) this.querySelector('[data-upsell-price]').replaceChildren(price.content.cloneNode(true));
    };
    this.select.addEventListener('change', update, {signal});
    // Capture locks the complete add/read/render interval, including submit events
    // without a submitter; the existing native aria-busy guard alone covers less.
    this.addEventListener('submit', event => {
      if (this.pending || !this.select.value || this.select.selectedOptions[0]?.disabled) { event.preventDefault(); event.stopImmediatePropagation(); return; }
      this.pending = true; this.button.disabled = true;
      this.querySelector('[data-upsell-error]').hidden = true;
      this.timer = setTimeout(() => {
        if (!this.pending || !this.isConnected) return;
        const error = this.querySelector('[data-upsell-error]');
        error.textContent = 'Your request is taking longer than expected. Check your bag before trying again.'; error.hidden = false;
        // An uncertain transport outcome must never enable an automatic retry.
      }, 10000);
    }, {capture: true, signal});
    const refreshUnavailable = () => {
      if (!this.pending || !this.confirmedCart || !this.isConnected) return;
      clearTimeout(this.timer);
      const error = this.querySelector('[data-upsell-error]');
      error.textContent = 'Added to your bag, but the bag could not refresh. Refresh the page to see it.'; error.hidden = false;
      // Keep Add locked: this item was added successfully, and must not be retried.
    };
    this.addEventListener('variant:add', event => {
      clearTimeout(this.timer); this.confirmedCart = event.detail?.cart;
      this.timer = setTimeout(refreshUnavailable, 6000);
    }, {signal});
    this.ownerDocument.addEventListener('cart-drawer:refresh-failed', event => {
      if (event.detail?.cart === this.confirmedCart) refreshUnavailable();
    }, {signal});
    this.addEventListener('cart:error', event => {
      clearTimeout(this.timer); this.pending = false; update();
      const error = this.querySelector('[data-upsell-error]'); error.textContent = event.detail?.error || 'This option could not be added. Please choose another.'; error.hidden = false;
    }, {signal});
    update();
  }
  disconnectedCallback() { this.events?.abort(); clearTimeout(this.timer); }
}
class RaithCartUpsells extends HTMLElement {
  static get observedAttributes() { return ['data-cart-products', 'data-source', 'data-url', 'data-country', 'data-currency']; }
  attributeChangedCallback() { if (this.isConnected && this.controller) this.schedule(); }
  connectedCallback() {
    this.controller?.abort(); this.controller = new AbortController();
    this.drawer = this.closest('cart-drawer');
    const signal = this.controller.signal;
    this.drawer?.addEventListener('cart-drawer:refreshed', () => this.schedule(), {signal});
    this.drawer?.addEventListener('dialog:before-show', event => { if (event.target === this.drawer) this.schedule(); }, {signal});
    this.drawer?.addEventListener('dialog:before-hide', event => { if (event.target === this.drawer) this.cancel(); }, {signal});
    this.schedule();
  }
  schedule() {
    if (this.queued) return;
    this.queued = true;
    // Native opening emits before-show and then reparents the drawer. Wait for
    // that synchronous reconnect instead of starting and aborting two reads.
    queueMicrotask(() => {
      this.queued = false;
      if (this.isConnected && !this.controller?.signal.aborted) this.load();
    });
  }
  cancel() { this.request?.abort(); this.request = null; this.loading = false; clearTimeout(this.timer); }
  disconnectedCallback() { this.controller?.abort(); this.cancel(); }
  async load() {
    const signature = () => {
      const s = window.Shopify;
      return JSON.stringify([this.dataset.cartProducts, this.dataset.source, this.dataset.url, this.dataset.country, this.dataset.currency,
        s?.shop, s?.theme?.id, s?.theme?.role, s?.country, s?.currency?.active, s?.locale, s?.routes?.root]);
    };
    const requestKey = signature();
    const rail = this.querySelector('[data-upsell-rail]');
    if (!rail || !this.isConnected || this.controller?.signal.aborted) return;
    if (this.requestKey !== requestKey) {
      this.cancel(); this.requestKey = requestKey; this.loadedKey = null;
      if (this.dataset.source !== 'curated') { this.hidden = true; rail.replaceChildren(); }
    }
    const ids = new Set((this.dataset.cartProducts || '').split(',').filter(Boolean));
    const filter = nodes => {
      const seen = new Set(ids);
      return nodes.filter(node => { const id = node.dataset.productId; if (!/^\d+$/.test(id || '') || seen.has(id)) return false; seen.add(id); return true; }).slice(0, 3);
    };
    if (this.dataset.source === 'curated') {
      this.cancel();
      const selected = filter([...rail.children]);
      [...rail.children].forEach(node => { if (!selected.includes(node)) node.remove(); });
      this.hidden = !selected.length; return;
    }
    const s = window.Shopify;
    if (!ids.size || s?.shop !== 'raithclothing.myshopify.com' || !/^[1-9]\d*$/.test(String(s.theme?.id)) || !['main', 'unpublished'].includes(s.theme?.role) || s.country !== this.dataset.country || s.currency?.active !== this.dataset.currency) {
      this.cancel(); this.loadedKey = null; this.hidden = true; rail.replaceChildren(); return;
    }
    // Hidden carts do not compete with the page for recommendation requests.
    // Native editor selection opens this same drawer, so it follows this path too.
    if (!this.drawer?.hasAttribute('open')) { this.cancel(); return; }
    const age = Date.now() - this.loadedAt;
    if (this.loadedKey === requestKey && age >= 0 && age < 300000) return;
    // Connection, native opening and refreshed can arrive in the same turn.
    if (this.loading && this.requestKey === requestKey && !this.request?.signal.aborted) return;
    this.cancel(); this.loadedKey = null; this.hidden = true; rail.replaceChildren();
    const request = this.request = new AbortController();
    this.loading = true;
    this.timer = setTimeout(() => request.abort(), 8000);
    const current = () => !request.signal.aborted && this.request === request && this.isConnected
      && this.drawer?.hasAttribute('open') && signature() === requestKey && window.Shopify === s
      && s.country === this.dataset.country && s.currency?.active === this.dataset.currency;
    try {
      const base = new URL(this.dataset.url, location.origin);
      if (base.origin !== location.origin) return;
      base.searchParams.set('section_id', 'raith-cart-upsells'); base.searchParams.set('limit', '10');
      base.searchParams.set('intent', this.dataset.source === 'complementary' ? 'complementary' : 'related');
      let selected = [], received = false;
      // A recently added first item can have no suggestions. Ask Shopify for up
      // to three basket products; only its native related/complementary results
      // may fill the rail. One deadline bounds the complete read sequence.
      for (const productId of [...ids].filter(id => /^\d+$/.test(id)).slice(0, 3)) {
        if (!current() || selected.length >= 3) break;
        const url = new URL(base); url.searchParams.set('product_id', productId);
        try {
          const response = await fetch(url, {credentials: 'same-origin', redirect: 'error', signal: request.signal});
          if (!response.ok) continue;
          const html = await response.text();
          if (!current()) return;
          const root = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-native-upsell-response]');
          if (!root || root.dataset.country !== this.dataset.country || root.dataset.currency !== this.dataset.currency) continue;
          received = true;
          const available = [...root.querySelectorAll('raith-cart-upsell-card')].filter(card =>
            [...card.querySelectorAll('select[name="id"] option')].some(option => option.value && !option.disabled));
          selected = filter([...selected, ...available]);
        } catch { if (!current()) return; }
      }
      if (!current()) return;
      if (received) { this.loadedKey = requestKey; this.loadedAt = Date.now(); }
      rail.replaceChildren(...selected); this.hidden = !selected.length;
    } catch { /* Empty/failed native recommendations remain hidden. */ }
    finally { if (this.request === request) { clearTimeout(this.timer); this.loading = false; } }

  }
}
if (!customElements.get('raith-cart-upsell-card')) customElements.define('raith-cart-upsell-card', RaithCartUpsellCard);
if (!customElements.get('raith-cart-upsells')) customElements.define('raith-cart-upsells', RaithCartUpsells);

